/**
 * Enums for the unified external system reference tracking table (ext_system_references).
 *
 * These types classify the *kind* of reference, where it came from, and what
 * entity it is attached to, replacing scattered ad-hoc fields like originId,
 * referenceIds[], customerPurchaseOrderNumber, etc.
 */

/** What kind of reference identifier this is. */
export enum ReferenceType {
  /** EDI qualifiers: CO, OQ */
  PurchaseOrder = "purchase_order",
  /** EDI qualifiers: VN, IA */
  SalesOrder = "sales_order",
  /** Internal transfer-order / stock-transfer document number */
  TransferOrder = "transfer_order",
  /** Invoice / voucher number */
  Invoice = "invoice",
  /** EDI qualifier: BM */
  BillOfLading = "bill_of_lading",
  /** Tracking / shipment / load number */
  Shipment = "shipment",
  /** Delivery confirmation / POD number */
  ProofOfDelivery = "proof_of_delivery",
  /** Catch-all for unrecognized reference types */
  Other = "other",
}

export interface ClassifiedReference {
  type: ReferenceType;
  value: string;
  raw: string;
}

/** How this reference entered the system. */
export enum ReferenceSource {
  /** Extracted from a document (invoice PDF, email attachment) by AI/OCR */
  DocumentExtraction = "document_extraction",
  /** Received via inbound EDI transaction */
  EdiInbound = "edi_inbound",
  /** Sent via outbound EDI transaction */
  EdiOutbound = "edi_outbound",
  /** Synced from an external ERP (Famous, QuickBooks, etc.) */
  ErpSync = "erp_sync",
  /** Manually entered by a user */
  ManualEntry = "manual_entry",
  /** Bulk import (CSV, spreadsheet, etc.) */
  Import = "import",
}

/**
 * Generic ERP document categories this reference can attach to.
 *
 * - These describe the kind of business document a reference points at.
 * - They are for document/reference semantics, not Gently's internal model
 *   layer.
 * - Distinct from `SystemEntity`, which represents concrete Gently ERP
 *   entities and records in our system.
 * - The names may overlap (`invoice`, `sales_order`, etc.), but the purpose
 *   is different.
 */
export enum ReferenceEntityType {
  Invoice = "invoice",
  SalesOrder = "sales_order",
  TransferOrder = "transfer_order",
  PurchaseOrder = "purchase_order",
  BillOfLading = "bill_of_lading",
  Shipment = "shipment",
  ProofOfDelivery = "proof_of_delivery",
}
